import { Badge, Input, PhoneInput, Typography } from '@smartgift/frontend-toolkit';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'react-phone-input-2/lib/style.css';

import { Card } from '@hero/atoms';
import { ChangePasswordEmailSentModal } from '@hero/components/modals';
import { DetailItem } from '@hero/components/molecules';
import { MAX_FIRST_NAME_LENGTH, MAX_LAST_NAME_LENGTH, ORGANIZATION_ROLES } from '@hero/constants';
import { useGetUser, useUpdateUser } from '@hero/services/organization-service/hooks';
import { formatPhoneNumber } from '@hero/utils';

import * as S from './style';
import { profileSchema } from './validationSchema';

function UserProfileForm({ organizationRole }) {
  const { t } = useTranslation();
  const { mutateAsync: onUserUpdate } = useUpdateUser();
  // const { mutateAsync: onRequestChangePassword } = useRequestChangePassword();
  const { data: user } = useGetUser();
  // const { showNotification, close } = useNotification();
  const [isEmailSent, setIsEmailSent] = useState(false);

  const formik = useFormik({
    initialValues: {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      phoneNumber: user?.phone ?? '',
      email: user?.email ?? ''
    },
    validationSchema: profileSchema
  });

  // const onSendResetPasswordEmail = async () => {
  //   await onRequestChangePassword({
  //     email: formik.values.email,
  //     merchantCode: configs.merchantName
  //   });
  //   setIsEmailSent(true);
  // };

  function LastNameEditInput() {
    return (
      <Input
        value={formik.values.lastName}
        inputProps={{
          maxLength: MAX_LAST_NAME_LENGTH,
          onChange: e => formik.setFieldValue('lastName', e.target.value)
        }}
        formItem={{
          placeholder: t('enterASenderName'),
          label: t('lastName'),
          name: 'lastName',
          validations: {
            required: {
              required: true,
              message: t('inputEmptyWarning', { name: t('lastName') })
            }
          }
        }}
      />
    );
  }

  function FirstNameEditInput() {
    return (
      <Input
        value={formik.values.firstName}
        inputProps={{
          maxLength: MAX_FIRST_NAME_LENGTH,
          onChange: e => formik.setFieldValue('firstName', e.target.value)
        }}
        formItem={{
          placeholder: t('enterASenderName'),
          label: t('firstName'),
          name: 'firstName',
          validations: {
            required: {
              required: true,
              message: t('inputEmptyWarning', { name: t('firstName') })
            }
          }
        }}
      />
    );
  }

  function PhoneEditInput() {
    return (
      <PhoneInput
        name="phone"
        label={`${t('phone')}*`}
        inputProps={{
          required: true,
          placeholder: t('phone')
        }}
        onChange={value => formik.setFieldValue('phoneNumber', value)}
        countryCodeEditable
        country={'us'}
        value={formik.values.phoneNumber}
      />
    );
  }

  return (
    <Card>
      <S.FormContent>
        <S.FormHeaderWrapper>
          <Typography variant="body1Bold">{t('contactInformation')}</Typography>
          {!!organizationRole && (
            <Badge color="bgDefault" variant="contained" size="sm" rounded>
              {ORGANIZATION_ROLES[organizationRole]}
            </Badge>
          )}
        </S.FormHeaderWrapper>
        <DetailItem
          title={`${t('firstName')}:`}
          withBorder
          bodyStyle={{
            display: 'flex',
            alignItems: 'center'
          }}
          editable
          closeWithoutSave={() => {
            formik.setFieldValue('firstName', user?.firstName);
          }}
          saveDetail={() => onUserUpdate({ firstName: formik.values.firstName })}
          editInput={FirstNameEditInput}
        >
          {formik.values.firstName}
        </DetailItem>
        <DetailItem
          title={`${t('lastName')}:`}
          withBorder
          bodyStyle={{
            display: 'flex',
            alignItems: 'center'
          }}
          editable
          closeWithoutSave={() => formik.setFieldValue('lastName', user?.lastName)}
          saveDetail={() => onUserUpdate({ lastName: formik.values.lastName })}
          editInput={LastNameEditInput}
        >
          {formik.values.lastName}
        </DetailItem>
        <DetailItem
          title={`${t('email')}:`}
          withBorder
          bodyStyle={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {formik.values.email}
        </DetailItem>
        <DetailItem
          title={`${t('phone')}:`}
          withBorder
          bodyStyle={{
            display: 'flex',
            alignItems: 'center',
            border: 'none'
          }}
          editable
          closeWithoutSave={() => formik.setFieldValue('phoneNumber', user?.phone)}
          saveDetail={() => onUserUpdate({ phoneNumber: formik.values.phoneNumber })}
          editInput={PhoneEditInput}
        >
          {formatPhoneNumber(formik.values.phoneNumber)}
        </DetailItem>

        {/* <DetailItem
          title={`${t('password')}:`}
          bodyStyle={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Button
            variant="link"
            onClick={() => {
              showNotification({
                header: t('changePassword'),
                customContent: () => (
                  <S.ChangePasswordModalBody>
                    <Icon name="login" size="xxl" color="warning" />
                    <Typography variant="body1Bold" emphasis="bold">
                      {t('areYouSure')}
                    </Typography>
                    <Typography variant="body2">{t('changePasswordDescription')}</Typography>
                  </S.ChangePasswordModalBody>
                ),
                primaryButtonProps: {
                  children: t('yesImSure'),
                  onClick: () => {
                    onSendResetPasswordEmail();
                  }
                },
                secondaryButtonProps: {
                  children: t('cancel'),
                  onClick: () => {
                    close();
                  }
                }
              });
            }}
          >
            {t('changePassword')}
          </Button>
        </DetailItem> */}
      </S.FormContent>
      <ChangePasswordEmailSentModal
        isOpen={isEmailSent}
        email={formik.values.email}
        onRequestClose={() => setIsEmailSent(false)}
      />
    </Card>
  );
}

UserProfileForm.propTypes = {
  organizationRole: PropTypes.string
};

export { UserProfileForm };
